import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset. You can now sign in.");
      navigate("/admin/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20 min-h-screen bg-[#F4F5F7] flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-md p-8">
        <h1 className="font-display text-2xl font-black uppercase text-white mb-2">Set New Password</h1>
        <p className="text-sm text-zinc-500 mb-6">Minimum 8 characters.</p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/40 text-red-400 text-sm rounded-sm px-4 py-3 mb-4" data-testid="reset-error">
            {error}
          </div>
        )}
        {!token && (
          <div className="bg-amber-500/10 border border-amber-500/40 text-amber-400 text-sm rounded-sm px-4 py-3 mb-4" data-testid="reset-no-token">
            This reset link is missing its token. Request a new one.
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">New Password</Label>
            <Input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-white border-zinc-300 text-zinc-900 focus-visible:ring-[#FF6B00] rounded-sm"
              data-testid="reset-password-input"
            />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Confirm Password</Label>
            <Input
              type="password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="bg-white border-zinc-300 text-zinc-900 focus-visible:ring-[#FF6B00] rounded-sm"
              data-testid="reset-confirm-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading || !token}
            className="w-full h-12 bg-[#FF6B00] hover:bg-[#FF8533] text-black font-bold uppercase tracking-wider rounded-sm"
            data-testid="reset-submit-button"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset Password"}
          </Button>
        </form>

        <Link to="/admin/login" className="block text-center text-xs text-zinc-500 hover:text-[#FF6B00] mt-5 transition-colors" data-testid="reset-back-to-login">
          Back to sign in
        </Link>
      </div>
    </div>
  );
}
